import React, { useState, useEffect } from 'react';
import { verify2FACode, send2FACode } from '../../../../services/twoFactorService';

const TwoFactorModal = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  userId, 
  userDetails, 
  methods = ['email'],
  receivedCode = null, // Add receivedCode prop to display the received code
  requireInitialSend = false // New prop to indicate if code needs to be sent first
}) => {
  const [code, setCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds
  const [resendTimeLeft, setResendTimeLeft] = useState(60); // 1 minute for resend
  const [canResend, setCanResend] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [fallbackCode, setFallbackCode] = useState(null);
  const [isFallbackMode, setIsFallbackMode] = useState(false);
  const [failedMethods, setFailedMethods] = useState([]);

  // Timer countdown
  useEffect(() => {
    if (!isOpen) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
      
      setResendTimeLeft(prev => {
        if (prev <= 1) {
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCode('');
      setError('');
      setTimeLeft(300);
      setCanResend(false);
      setCodeSent(!requireInitialSend); // If requireInitialSend is false, code is already sent
      setFallbackCode(null);
      setIsFallbackMode(false);
      setFailedMethods([]);
    }
  }, [isOpen, requireInitialSend]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setCode(value);
      setError('');
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const result = await verify2FACode(userId, code);
      
      if (result.success) {
        onSuccess();
      } else {
        setError(result.message);
        // Add a small delay to ensure loading state is visible before showing error
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    } catch (error) {
      setError('An error occurred during verification');
      // Add a small delay to ensure loading state is visible before showing error
      await new Promise(resolve => setTimeout(resolve, 1000));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSendCode = async () => {
    setIsSending(true);
    setError('');

    try {
      // Create user details with the correct 2FA methods
      const userDetailsWithMethods = {
        ...userDetails,
        twoFactorMethod: methods.join(',')
      };
      const result = await send2FACode(userId, userDetailsWithMethods);
      
      if (result.success) {
        setCodeSent(true);
        setTimeLeft(300); // Reset to 5 minutes
        setResendTimeLeft(60); // Reset resend timer to 1 minute
        setCanResend(false);
        setCode('');
        
        // Check if fallback mode is active
        if (result.fallback && result.code) {
          setFallbackCode(result.code);
          setIsFallbackMode(true);
          setFailedMethods(result.failedMethods || []);
        }
        // Code sent successfully
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Failed to send code');
    } finally {
      setIsSending(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setError('');

    try {
      // Create user details with the correct 2FA methods
      const userDetailsWithMethods = {
        ...userDetails,
        twoFactorMethod: methods.join(',')
      };
      const result = await send2FACode(userId, userDetailsWithMethods);
      
      if (result.success) {
        setTimeLeft(300); // Reset to 5 minutes
        setResendTimeLeft(60); // Reset resend timer to 1 minute
        setCanResend(false);
        setCode('');
        
        // Check if fallback mode is active
        if (result.fallback && result.code) {
          setFallbackCode(result.code);
          setIsFallbackMode(true);
          setFailedMethods(result.failedMethods || []);
        }
        // Code sent successfully - the parent component will handle the success message
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Failed to resend code');
    } finally {
      setIsResending(false);
    }
  };

  const getMethodDisplay = () => {
    return `email to ${userDetails?.email || 'your email'}`;
  };

  const getFallbackMessage = () => {
    return {
      title: "Email Service Temporarily Unavailable",
      message: "The email verification service is temporarily unavailable. Please use the code below:"
    };
  };

  if (!isOpen) return null;

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    <div onClick={onClose} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '32px',
        maxWidth: '450px',
        width: '90%',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
        position: 'relative'
      }}>
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: '#fff',
            border: '1px solid rgba(44,37,30,0.06)',
            fontSize: '16px',
            cursor: 'pointer',
            color: '#2E2D2D',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
            fontWeight: 'bold',
            lineHeight: 1
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#C5A572';
            e.target.style.color = '#FEFEFE';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#fff';
            e.target.style.color = '#2E2D2D';
          }}
        >
          ✕
        </button>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{
            width: '60px',
            height: '60px',
            backgroundColor: '#C5A572',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px auto',
            fontSize: '24px'
          }}>
            🔐
          </div>
          <h2 style={{
            margin: '0 0 8px 0',
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#333'
          }}>
            Two-Factor Authentication
          </h2>
          <p style={{
            margin: '0',
            fontSize: '14px',
            color: '#666',
            lineHeight: '1.4'
          }}>
            {codeSent 
              ? `Enter the 6-digit code sent to ${getMethodDisplay()}`
              : `We'll send a 6-digit code to ${getMethodDisplay()} for verification`
            }
          </p>
        </div>

        {/* Code Display (if available) */}
        {receivedCode && (
          <div style={{
            backgroundColor: '#E3F2FD',
            border: '1px solid #BBDEFB',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <p style={{
                fontSize: '14px',
                color: '#1976D2',
                margin: '0 0 8px 0'
              }}>
                Your verification code:
              </p>
              <div style={{
                fontSize: '32px',
                fontFamily: 'monospace',
                fontWeight: 'bold',
                color: '#0D47A1',
                letterSpacing: '4px',
                margin: '0'
              }}>
                {receivedCode}
              </div>
              <p style={{
                fontSize: '12px',
                color: '#1976D2',
                margin: '8px 0 0 0'
              }}>
                Check your email for this code
              </p>
            </div>
          </div>
        )}

        {/* Fallback Code Display (when services are down) */}
        {isFallbackMode && fallbackCode && (
          <div style={{
            backgroundColor: '#FFF3E0',
            border: '2px solid #FFB74D',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '16px',
                color: '#E65100',
                fontWeight: 'bold',
                margin: '0 0 8px 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}>
                ⚠️ {getFallbackMessage().title}
              </div>
              <p style={{
                fontSize: '14px',
                color: '#BF360C',
                margin: '0 0 12px 0'
              }}>
                {getFallbackMessage().message}
              </p>
              <div style={{
                fontSize: '36px',
                fontFamily: 'monospace',
                fontWeight: 'bold',
                color: '#D84315',
                letterSpacing: '6px',
                margin: '0',
                padding: '12px',
                backgroundColor: '#FFECB3',
                borderRadius: '8px',
                border: '2px dashed #FF8F00'
              }}>
                {fallbackCode}
              </div>
            </div>
          </div>
        )}

        {/* Code input form or Send Code button */}
        {codeSent ? (
          <form onSubmit={handleVerify}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#333',
                textAlign: 'center'
              }}>
                Verification Code
              </label>
              <input
                type="text"
                value={code}
                onChange={handleCodeChange}
                placeholder="000000"
                style={{
                  width: 'calc(100% - 32px)',
                  maxWidth: '200px',
                  padding: '12px 16px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  letterSpacing: '4px',
                  border: error ? '2px solid #dc3545' : '2px solid #ddd',
                  borderRadius: '8px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  margin: '0 auto',
                  display: 'block'
                }}
                maxLength={6}
                autoComplete="off"
                autoFocus
              />
              {error && (
                <div style={{
                  color: '#dc3545',
                  fontSize: '12px',
                  marginTop: '4px',
                  textAlign: 'center'
                }}>
                  {error}
                </div>
              )}
            </div>

            {/* Timer */}
            {timeLeft > 0 && (
              <div style={{
                textAlign: 'center',
                marginBottom: '20px',
                fontSize: '14px',
                color: '#666'
              }}>
                Code expires in: <strong style={{ color: '#C5A572' }}>{formatTime(timeLeft)}</strong>
              </div>
            )}

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="submit"
                disabled={isVerifying || code.length !== 6}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: isVerifying || code.length !== 6 ? '#E0D6C3' : '#C5A572',
                  color: isVerifying || code.length !== 6 ? '#999' : '#FDF9F3',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: isVerifying || code.length !== 6 ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {isVerifying ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #999',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    <span>Verifying...</span>
                  </>
                ) : (
                  'Verify Code'
                )}
              </button>
            </div>
          </form>
        ) : (
          /* Send Code Button */
          <div style={{ 
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <button
              onClick={handleSendCode}
              disabled={isSending}
              style={{
                padding: '12px 24px',
                backgroundColor: isSending ? '#E0D6C3' : '#C5A572',
                color: isSending ? '#999' : '#FDF9F3',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: isSending ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                minWidth: '200px'
              }}
            >
              {isSending ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #999',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <span>Sending...</span>
                </>
              ) : (
                'Send Verification Code'
              )}
            </button>
            {error && (
              <div style={{
                color: '#dc3545',
                fontSize: '12px',
                marginTop: '8px',
                textAlign: 'center'
              }}>
                {error}
              </div>
            )}
          </div>
        )}

        {/* Resend code - only show when code has been sent */}
        {codeSent && (
          <div style={{
            textAlign: 'center',
            marginTop: '20px',
            paddingTop: '20px',
            borderTop: '1px solid #eee'
          }}>
            <p style={{
              margin: '0 0 12px 0',
              fontSize: '14px',
              color: '#666'
            }}>
              Didn't receive the code?
            </p>
            <button
              onClick={handleResendCode}
              disabled={isResending || !canResend}
              style={{
                background: 'none',
                border: 'none',
                color: canResend && !isResending ? '#C5A572' : '#ccc',
                textDecoration: 'underline',
                cursor: canResend && !isResending ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              {isResending ? 'Sending...' : 'Resend Code'}
            </button>
            {!canResend && timeLeft > 0 && (
              <div style={{
                fontSize: '12px',
                color: '#999',
                marginTop: '4px'
              }}>
                Resend available in {formatTime(resendTimeLeft)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default TwoFactorModal;
